import { extendObservable, action } from 'mobx';
import { browserHistory } from 'react-router';
import FetchService from '../../../core/services/FetchService';
import FormService from '../../../core/services/FormService';
import NotificationService from '../../../core/services/NotificationService';
import { isRequired, isValidEmail } from '../../../core/validations';
import { omit } from 'lodash';

export default class StudentFormService {
  fetch = new FetchService();
  submit = new FetchService();
  form = new FormService();

  constructor() {
    extendObservable(this, {
      studentId: null,
      schoolId: null,
      classId: null,
      isPaymentDialogOpen: false,
    });
    this.form.validations = {
      name: [isRequired],
      academicPlan: [isRequired],
      locale: [isRequired],
      email: [isRequired, isValidEmail],
      accessStartsAt: [(value, form) => (form.demoStudent ? isRequired(value) : null)],
      accessEndsAt: [(value, form) => (form.demoStudent ? isRequired(value) : null)],
    };
  }

  handleLoad = action((studentId, schoolId, classId, academicPlanId, locale) => {
    this.schoolId = schoolId;
    this.classId = classId;
    this.form.reset();
    if (studentId) {
      this.fetch
        .fetch({
          url: `/students/${studentId}`,
        })
        .then(() => {
          if (this.fetch.data) {
            const data = {
              ...this.fetch.data,
              ...(this.fetch.data.currentCourse && {
                currentCourse: this.fetch.data.currentCourse.id,
              }),
              ...(this.fetch.data.academicPlan && {
                academicPlan: this.fetch.data.academicPlan.id,
              }),
              ...(!this.fetch.data.locale && {
                locale: this.fetch.data.schoolClass.school.locale,
              }),
            };
            this.form.setInitialValues(data);
          }
        });
    } else {
      this.form.validations.password = [isRequired];
      this.form.setInitialValues({
        sendWelcomeEmail: false,
        demoStudent: false,
        academicPlan: academicPlanId,
        locale,
      });
    }
    this.studentId = studentId;
  });

  handleSubmit = action(() => {
    this.form.submitted = true;
    if (this.form.errors) {
      NotificationService.addNotification('Fill the required fields', 'error');
      return;
    }
    const studentId = this.form.getValue('id');
    if (studentId && !this.form.getValue('schoolClass.id')) {
      NotificationService.addNotification('The school class is required', 'error');
      return;
    }

    this.submit
      .fetch({
        method: studentId ? 'put' : 'post',
        url: studentId ? `/students/${studentId}` : `/schools/${this.schoolId}/classes/${this.classId}/students`,
        body: {
          ...omit(this.form.getValues(), 'company'),
          cpf: !this.form.getValue('cpf') ? null : this.form.getValue('cpf'),
          contactPhone: !this.form.getValue('contactPhone') ? null : this.form.getValue('contactPhone'),
          schoolClass: this.form.getValue('schoolClass.id'),
        },
      })
      .then(() => {
        if (this.submit.data) {
          const student = this.submit.data;
          this.studentId = student.id;

          if (this.submit.data.status === 'AWAITING_PAYMENT') {
            this.isPaymentDialogOpen = true;
            this.form.setValue('paymentGatewayInvoiceUrl', this.submit.data.paymentGatewayInvoiceUrl);
          } else {
            browserHistory.goBack();
            NotificationService.addNotification(
              `Student ${studentId ? 'updated' : 'created'} successfully.`,
              'success'
            );
          }
        }
        if (this.submit.error) {
          if (
            this.submit.response.data.error === 'duplicated_data' ||
            (this.submit.error && this.submit.error.indexOf('E11000') > -1)
          ) {
            NotificationService.addNotification('We already have a student with this email.', 'error');
          } else {
            NotificationService.addNotification(`Error ${studentId ? 'updating' : 'creating'} student.`, 'error');
          }
        }
      });
  });

  handleClosePaymentDialog = action(() => {
    this.isPaymentDialogOpen = false;
    this.form.reset();
    browserHistory.goBack();
  });
}
